import React, { useEffect, useRef, useState } from "react";
import {
  ShieldCheckIcon,
  ClockIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline";
 
const WhyChooseUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const tableRef = useRef(null);
 
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2, rootMargin: '0px' }
    );
 
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
 
    return () => observer.disconnect();
  }, []);
 
  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-b from-gray-50 to-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       
        <div className={`text-center max-w-2xl mx-auto mb-12 transition-all duration-1000 transform ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <span className="text-sm font-medium text-blue-600 uppercase tracking-wider relative inline-block">
            Why choose us
            <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-200 scale-0 transition-transform duration-700 delay-300 origin-left"
                  style={{ transform: isVisible ? 'scaleX(1)' : 'scaleX(0)' }}></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mt-2 mb-3">
            What makes DonatePe different
          </h2>
          <p className="text-gray-500">
            We've built features that put campaigners first. No one else does it like we do.
          </p>
        </div>
       
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: '4% fee(On Self Crowdfunding )',
              description: 'Unlike others who take 2-5%, DonatePe takes nothing. You keep 100%.',
              icon: ShieldCheckIcon,
              color: 'blue',
              stats: 'Save ₹5,000 on a ₹1L campaign'
            },
            {
              title: '24/7 fund access',
              description: 'Access your funds anytime, day or night. No waiting periods.',
              icon: ClockIcon,
              color: 'emerald',
              stats: 'Money available 24/7'
            },
            {
              title: 'Global reach',
              description: 'Accept donations from anywhere in the world. Multiple currencies.',
              icon: GlobeAltIcon,
              color: 'amber',
              stats: 'Donors from 30+ countries'
            },
            {
              title: 'Campaign coaching',
              description: 'Free guidance from our team to help you reach your goal faster.',
              icon: AcademicCapIcon,
              color: 'purple',
              stats: '90% campaigns succeed with help'
            },
            {
              title: 'Recurring giving',
              description: 'Enable monthly donations for sustained support to your cause.',
              icon: ClockIcon,
              color: 'pink',
              stats: 'Average donor gives 3x more'
            },
            {
              title: 'Real-time tracking',
              description: 'See exactly who donates and when. Full transparency.',
              icon: ChartBarIcon,
              color: 'indigo',
              stats: 'Live dashboard for every campaign'
            }
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                ref={(el) => (cardRefs.current[index] = el)}
                className={`group relative transition-all duration-700 hover:z-10 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-${item.color}-50 rounded-2xl blur-xl opacity-0 group-hover:opacity-60 transition-all duration-500 group-hover:scale-110`}></div>
                <div className="relative bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] flex flex-col items-center text-center">
                  <div className="relative mb-3">
                    <Icon className={`w-8 h-8 text-${item.color}-600 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3`} />
                   
                    {/* Floating particles on hover - hidden on mobile for better performance */}
                    <div className="hidden md:block absolute -top-2 -right-2 w-2 h-2 rounded-full bg-${item.color}-400 opacity-0 group-hover:opacity-100 transition-all duration-500 animate-ping"></div>
                    <div className="hidden md:block absolute -top-1 -right-1 w-1 h-1 rounded-full bg-${item.color}-300 opacity-0 group-hover:opacity-100 transition-all duration-500 animate-ping delay-100"></div>
                  </div>
                 
                  <h3 className={`text-lg font-medium text-${item.color}-600 mb-2 group-hover:translate-x-0 transition-transform duration-300`}>
                    {item.title}
                  </h3>
                 
                  <p className="text-gray-500 text-sm leading-relaxed mb-3 group-hover:text-gray-600 transition-colors duration-300">
                    {item.description}
                  </p>
                 
                  <div className="inline-block transform transition-all duration-300 group-hover:scale-105 group-hover:bg-${item.color}-50">
                    <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full inline-block group-hover:text-gray-600 group-hover:bg-white transition-all duration-300">
                      {item.stats}
                    </span>
                  </div>
                 
                  {/* Bottom gradient line on hover - works on all devices */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-${item.color}-400 to-${item.color}-600 group-hover:w-full transition-all duration-500 rounded-full`}></div>
                 
                  {/* Mobile touch feedback - subtle background change on tap */}
                  <div className="md:hidden absolute inset-0 bg-black opacity-0 active:opacity-5 transition-opacity duration-200 rounded-xl pointer-events-none"></div>
                </div>
              </div>
            );
          })}
        </div>
       
        {/* Comparison table teaser */}
        <div
          ref={tableRef}
          className={`mt-12 bg-white p-6 rounded-xl border border-gray-100 max-w-3xl mx-auto transition-all duration-1000 hover:shadow-2xl hover:border-blue-100 md:hover:scale-[1.02] ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          <h3 className="font-medium text-gray-900 mb-4 text-center relative">
            DonatePe vs traditional platforms
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-blue-200 rounded-full"></span>
          </h3>
         
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div className="text-gray-400 transform transition-all duration-300 md:hover:scale-105">Platform</div>
            <div className="font-medium text-blue-600 transform transition-all duration-300 md:hover:scale-105 md:hover:text-blue-700">DonatePe</div>
            <div className="font-medium text-gray-400 transform transition-all duration-300 md:hover:scale-105">Others</div>
           
            {[
              { label: 'Platform fee', donatepe: '4%(On Self Crowdfunding )', others: '4-5%' },
              { label: 'Fund access', donatepe: '24/7', others: 'Business days only' },
              { label: 'Support', donatepe: '24/7 free', others: 'Paid plans' }
            ].map((row, idx) => (
              <React.Fragment key={idx}>
                <div className="text-gray-600 py-2 transform transition-all duration-300 md:hover:scale-105 md:hover:text-gray-900">{row.label}</div>
                <div className="text-emerald-600 font-medium py-2 transform transition-all duration-300 md:hover:scale-105 md:hover:text-emerald-700">{row.donatepe}</div>
                <div className="text-gray-500 py-2 transform transition-all duration-300 md:hover:scale-105 md:hover:text-gray-700">{row.others}</div>
              </React.Fragment>
            ))}
          </div>
         
          {/* Animated background pattern on hover - hidden on mobile */}
          <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-blue-50/0 via-blue-50/0 to-transparent rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
        </div>
      </div>
 
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
       
        .group:hover .animate-float {
          animation: float 2s ease-in-out infinite;
        }
 
        /* Ensure hover effects work on mobile devices that support hover */
        @media (hover: hover) {
          .group:hover .absolute.bottom-0 {
            width: 100%;
          }
        }
 
        /* For devices that don't support hover (most mobile devices),
           we use active state as fallback */
        @media (hover: none) {
          .group:active .absolute.bottom-0 {
            width: 100%;
          }
         
          .group:active {
            transform: translateY(-2px) scale(1.02);
          }
         
          .group:active .w-8.h-8 {
            transform: scale(1.1) rotate(3deg);
          }
         
          .group:active .inline-block {
            transform: scale(1.05);
          }
        }
      `}</style>
    </section>
  );
};
 
export default WhyChooseUs;